import { CommonEvent } from '@/constant/events'

import type { DialogConfig } from '@/types/common'

export interface OrgTagUpdatedPayload {
  /** 刪除的分類標記 ID 陣列 */
  deletedTagIds: number[]
}

export interface TriggerListRefreshOptions {
  /**
   * 指定要刷新的目標列表名稱
   * 不傳時會刷新所有監聽的列表
   */
  targetList?: string
  /**
   * 可選的刪除項目 ID，傳入時會使用前端刪除，不傳時會完整刷新
   */
  removedId?: number | string
}

type EventList = {
  [CommonEvent.OPEN_DIALOG]: [DialogConfig]
  [CommonEvent.CLOSE_DIALOG]: []
  [CommonEvent.REFRESH_LIST]: [payload: TriggerListRefreshOptions | undefined]
  [CommonEvent.ORG_TAG_UPDATED]: [payload: OrgTagUpdatedPayload]
}

type Listener<T extends unknown[]> = (...args: T) => void

class EventEmitter<EventMap extends Record<string | number, unknown[]>> {
  private events: {
    [K in keyof EventMap]?: Array<Listener<EventMap[K]>>
  } = {}

  on<K extends keyof EventMap>(event: K, callback: Listener<EventMap[K]>) {
    if (!this.events[event]) {
      this.events[event] = []
    }
    this.events[event]?.push(callback)
  }

  emit<K extends keyof EventMap>(event: keyof EventMap, ...args: EventMap[K]) {
    this.events[event]?.forEach((listener) => listener(...args))
  }

  off<K extends keyof EventMap>(event: K, callback?: Listener<EventMap[K]>) {
    if (!callback) {
      // 如果沒有提供 callback，刪除該事件的所有監聽器
      delete this.events[event]
    } else {
      // 如果提供了 callback，只刪除該特定監聽器
      const listeners = this.events[event]
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
        // 如果沒有監聽器了，刪除該事件
        if (listeners.length === 0) {
          delete this.events[event]
        }
      }
    }
  }
}

export const eventEmitter = new EventEmitter<EventList>()
